import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Inbox, FileWarning, UserCheck, AlertCircle, Trash2 } from "lucide-react";
import { ConfirmModal } from "@/components/ConfirmModal";

type TabId = "staging" | "cartas" | "reps";

interface PageResp<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
}

export function CalidadPage() {
  const [tab, setTab] = useState<TabId>("staging");

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold text-fg">Bandejas de Registro Legal</h1>
        <p className="text-sm text-fg-muted">
          Trabajo pendiente del equipo Calidad sobre el módulo de Registro Legal.
        </p>
      </header>

      <div className="flex gap-1 rounded-lg border bg-surface p-1 text-sm">
        <TabButton tab={tab} id="staging" onClick={setTab} icon={<Inbox className="h-4 w-4" />} label="Importación pendiente" />
        <TabButton tab={tab} id="cartas" onClick={setTab} icon={<FileWarning className="h-4 w-4" />} label="Cartas sin documento" />
        <TabButton tab={tab} id="reps" onClick={setTab} icon={<UserCheck className="h-4 w-4" />} label="Reps sin instrumento" />
      </div>

      <div>
        {tab === "staging" && <StagingTab />}
        {tab === "cartas" && <CartasTab />}
        {tab === "reps" && <RepsTab />}
      </div>
    </div>
  );
}

function TabButton({
  tab,
  id,
  onClick,
  icon,
  label,
}: {
  tab: TabId;
  id: TabId;
  onClick: (t: TabId) => void;
  icon: React.ReactNode;
  label: string;
}) {
  const active = tab === id;
  return (
    <button
      onClick={() => onClick(id)}
      className={`flex flex-1 items-center justify-center gap-2 rounded px-3 py-2 ${
        active ? "bg-primary text-white shadow" : "text-fg-muted hover:bg-surface-2"
      }`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

// -----------------------------------------------------------------------------
// Bandeja: Staging
// -----------------------------------------------------------------------------
interface StagingRow {
  id: string;
  origen: string;
  hojaOrigen: string;
  filaOrigen: number | null;
  tipoRegistro: string;
  razonSocial: string;
  rfc: string;
  estadoResolucion: string;
  motivoStaging: string;
  importadoEn: string;
}

function StagingTab() {
  const qc = useQueryClient();
  const [descartar, setDescartar] = useState<StagingRow | null>(null);
  const q = useQuery({
    queryKey: ["staging"],
    queryFn: async () => (await api.get<PageResp<StagingRow>>("/api/admin/staging?estado=PENDIENTE&size=50")).data,
  });
  return (
    <>
      <DataTable
        query={q}
        empty="No hay filas pendientes de importación."
        headers={["Razón social", "RFC", "Hoja", "Estado", "Motivo", "Acciones"]}
        rows={(d) =>
          d.content.map((r) => ({
            key: r.id,
            to: null,
            cells: [
              <span className="font-medium">{r.razonSocial}</span>,
              <span className="font-mono text-xs">{r.rfc}</span>,
              r.hojaOrigen,
              <Badge tone="warn">{r.estadoResolucion}</Badge>,
              <span className="text-xs text-fg-muted">{r.motivoStaging}</span>,
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setDescartar(r);
                }}
                className="inline-flex items-center gap-1 rounded border border-estado-err/25 px-2 py-1 text-xs text-estado-err hover:bg-estado-err-bg"
              >
                <Trash2 className="h-3 w-3" />
                Descartar
              </button>,
            ],
          }))
        }
        total={(d) => d.totalElements}
      />
      <ConfirmModal
        open={!!descartar}
        title="Descartar fila de importación"
        body={
          descartar && (
            <>
              Vas a descartar <span className="font-medium">{descartar.razonSocial}</span>{" "}
              (<span className="font-mono">{descartar.rfc}</span>). Esta acción NO es
              reversible desde la UI; la fila quedará marcada como descartada.
            </>
          )
        }
        motivoLabel="Motivo del descarte"
        motivoRequired
        confirmLabel="Descartar"
        confirmTone="danger"
        onCancel={() => setDescartar(null)}
        onConfirm={async (motivo) => {
          await api.post(`/api/admin/staging/${descartar!.id}/descartar`, { motivo });
          setDescartar(null);
          await qc.invalidateQueries({ queryKey: ["staging"] });
        }}
      />
    </>
  );
}

// -----------------------------------------------------------------------------
// Bandeja: Cartas sin documento
// -----------------------------------------------------------------------------
interface CartaRow {
  id: string;
  titularId: string;
  titularRazonSocial: string;
  titularRfc: string;
  tramitadorRazonSocial: string | null;
  sinVencimiento: boolean;
  vigenciaFin: string | null;
  estatus: string;
}

function CartasTab() {
  const q = useQuery({
    queryKey: ["cartas-sin-doc"],
    queryFn: async () => (await api.get<CartaRow[]>("/api/cartas-poder/sin-documento")).data,
  });
  return (
    <DataTable
      query={q}
      empty="Todas las cartas vigentes tienen documento."
      headers={["Titular", "RFC", "Tramitador", "Vigencia", "Estatus"]}
      rows={(d) =>
        d.map((r) => ({
          key: r.id,
          to: `/cartas-poder/${r.id}`,
          cells: [
            <span className="font-medium">{r.titularRazonSocial}</span>,
            <span className="font-mono text-xs">{r.titularRfc}</span>,
            r.tramitadorRazonSocial ?? "—",
            r.sinVencimiento ? "Sin vencimiento" : r.vigenciaFin?.slice(0, 10) ?? "—",
            <Badge tone="info">{r.estatus}</Badge>,
          ],
        }))
      }
      total={(d) => d.length}
    />
  );
}

// -----------------------------------------------------------------------------
// Bandeja: Representantes sin instrumento
// -----------------------------------------------------------------------------
interface RepRow {
  id: string;
  registroLegalId: string;
  nombreCompleto: string;
  correo: string | null;
  rfc: string | null;
  estatus: string;
  vigenciaInicio: string;
  vigenciaFin: string | null;
}

function RepsTab() {
  const q = useQuery({
    queryKey: ["reps-sin-instrumento"],
    queryFn: async () => (await api.get<RepRow[]>("/api/representantes-legales/sin-instrumento")).data,
  });
  return (
    <DataTable
      query={q}
      empty="Todos los representantes activos tienen instrumento de poder."
      headers={["Nombre", "RFC", "Correo", "Vigencia inicio", "Estatus"]}
      rows={(d) =>
        d.map((r) => ({
          key: r.id,
          to: `/representantes-legales/${r.id}`,
          cells: [
            <span className="font-medium">{r.nombreCompleto}</span>,
            <span className="font-mono text-xs">{r.rfc ?? "—"}</span>,
            r.correo ?? "—",
            r.vigenciaInicio?.slice(0, 10),
            <Badge tone="info">{r.estatus}</Badge>,
          ],
        }))
      }
      total={(d) => d.length}
    />
  );
}

// -----------------------------------------------------------------------------
// Helpers de tabla
// -----------------------------------------------------------------------------
interface RowSpec {
  key: string;
  to: string | null;
  cells: React.ReactNode[];
}

function DataTable<T>({
  query,
  empty,
  headers,
  rows,
  total,
}: {
  query: { isLoading: boolean; error: unknown; data: T | undefined };
  empty: string;
  headers: string[];
  rows: (data: T) => RowSpec[];
  total: (data: T) => number;
}) {
  const navigate = useNavigate();
  if (query.isLoading) {
    return <div className="rounded-lg border bg-surface p-6 text-sm text-fg-subtle">Cargando…</div>;
  }
  if (query.error) {
    return (
      <div className="flex items-start gap-2 rounded-lg border bg-estado-warn-bg p-4 text-sm text-estado-warn">
        <AlertCircle className="mt-0.5 h-4 w-4" />
        <div>Error cargando la bandeja.</div>
      </div>
    );
  }
  if (!query.data) return null;
  const rs = rows(query.data);
  return (
    <div className="rounded-lg border bg-surface shadow-card">
      <div className="border-b px-4 py-2 text-xs uppercase tracking-wide text-fg-subtle">
        {total(query.data)} elementos
      </div>
      {rs.length === 0 ? (
        <div className="p-6 text-center text-sm text-fg-subtle">{empty}</div>
      ) : (
        <div className="w-full overflow-x-auto">
        <table className="min-w-full divide-y text-sm">
          <thead className="bg-surface-2">
            <tr>
              {headers.map((h) => (
                <th key={h} className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-fg-subtle">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y bg-surface">
            {rs.map((r) => (
              <tr
                key={r.key}
                className={r.to ? "cursor-pointer hover:bg-surface-2" : ""}
                onClick={r.to ? () => navigate(r.to!) : undefined}
              >
                {r.cells.map((c, i) => (
                  <td key={i} className="px-4 py-2 align-top">
                    {c}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

function Badge({ tone, children }: { tone: "ok" | "info" | "warn"; children: React.ReactNode }) {
  const cls = {
    ok: "bg-estado-ok-bg text-estado-ok",
    info: "bg-estado-info-bg text-estado-info",
    warn: "bg-estado-warn-bg text-estado-warn",
  }[tone];
  return <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{children}</span>;
}
